import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { hooks } from '../hooks';
import { components } from '../components';
import { authService, TenantOption } from '../services/authService';
import { setTokens } from '../store/slices/authSlice';
import { useLanguage } from '../context/LanguageContext';

interface LocationState {
  tempToken: string;
  tenants: TenantOption[];
}

export const TenantSelect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = hooks.useAppDispatch();
  const { t } = useLanguage();

  const state = location.state as LocationState | null;

  const [isLoading, setIsLoading] = React.useState(false);
  const [loadingTenantId, setLoadingTenantId] = React.useState<number | null>(null);
  const [errorMessage, setErrorMessage] = React.useState('');

  // Redirect kalau tidak ada state — user akses langsung tanpa login
  React.useEffect(() => {
    if (!state?.tempToken || !state?.tenants?.length) {
      navigate('/SignIn', { replace: true });
    }
  }, [state, navigate]);

  if (!state?.tempToken || !state?.tenants?.length) return null;

  const handleSelectTenant = async (tenant_id: number) => {
    try {
      setIsLoading(true);
      setLoadingTenantId(tenant_id);
      setErrorMessage('');

      const response = await authService.selectTenant(state.tempToken, tenant_id);

      if (response.accessToken && response.refreshToken) {
        dispatch(
          setTokens({
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            userId: response.user?.id,
            userName: response.user?.name,
            email: response.user?.email,
            groupId: (response.user as any)?.group_id,
            permissions: (response.user as any)?.permissions || [],
          })
        );
        navigate('/Task', { replace: true });
      }
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Failed to select workspace');
    } finally {
      setIsLoading(false);
      setLoadingTenantId(null);
    }
  };

  return (
    <div id='screen'>
      <components.Background />
      <components.Header />
      <main
        className='container'
        style={{ marginTop: 52, paddingTop: '18%', paddingBottom: 20, zIndex: 1 }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <img src="images/logo.png" alt="logo" style={{ width: 120, objectFit: 'contain' }} />
        </div>

        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
            {(t as any).tenantSelect?.title || 'Select Workspace'}
          </div>
          <div style={{ fontSize: 14, color: '#888' }}>
            {(t as any).tenantSelect?.subtitle || 'Choose the workspace to sign in to'}
          </div>
        </div>

        {errorMessage && (
          <div
            style={{
              backgroundColor: '#FFE0E0',
              color: '#C00000',
              padding: '10px 15px',
              borderRadius: 8,
              marginBottom: 15,
              fontSize: 14,
            }}
          >
            {errorMessage}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {state.tenants.map((tenant) => {
            const isThisLoading = isLoading && loadingTenantId === tenant.tenant_id;
            return (
              <button
                key={tenant.tenant_id}
                onClick={() => !isLoading && handleSelectTenant(tenant.tenant_id)}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  borderRadius: 12,
                  border: '2px solid #FF8A71',
                  backgroundColor: isThisLoading ? '#FF8A71' : '#fff',
                  color: isThisLoading ? '#fff' : '#FF8A71',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading && !isThisLoading ? 0.5 : 1,
                  transition: 'all 0.2s',
                  textAlign: 'left',
                }}
              >
                {isThisLoading
                  ? ((t as any).tenantSelect?.loading || 'Signing in...')
                  : tenant.tenant_name}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <span
            onClick={() => navigate('/SignIn', { replace: true })}
            style={{ fontSize: 14, color: '#888', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {(t as any).tenantSelect?.back || 'Back to login'}
          </span>
        </div>
      </main>
    </div>
  );
};
